import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useDestinatariByIds } from '@/hooks/useOrdini'

/**
 * Step 3: Assegnazione Righe Ordine ai Colli
 * 
 * Permette di assegnare le righe ordine ai colli creati nello step 2
 * Validazione: tutte le righe devono essere assegnate completamente
 */
export default function SpedizioneWizardStep3({ data, ordine, onComplete, onBack }) {
  const soloBoxPersonalizzata = ordine.tipo === 2
  const [assegnazioni, setAssegnazioni] = useState({})
  const [errors, setErrors] = useState([])

  const destinatariById = useDestinatariByIds(data.colli?.map((collo) => collo.destinatario_id))

  const getDestinatarioNome = (destinatarioId) => {
    const d = destinatariById.get(Number(destinatarioId))
    return d ? d.nome_completo : null
  }

  // Calcola quantità già spedite per ogni riga ordine
  const getQuantitaGiaSpedita = (rigaId) => {
    let totaleSpedito = 0
    
    // Somma quantità da tutte le spedizioni esistenti
    if (ordine.spedizioni && ordine.spedizioni.length > 0) {
      ordine.spedizioni.forEach((spedizione) => {
        if (spedizione.colli && spedizione.colli.length > 0) {
          spedizione.colli.forEach((collo) => {
            // NOTA: L'API restituisce 'righe_ordine' non 'contenuto'
            if (collo.righe_ordine && collo.righe_ordine.length > 0) {
              collo.righe_ordine.forEach((item) => {
                // Confronto con conversione a numero per sicurezza
                if (Number(item.riga_ordine) === Number(rigaId)) {
                  totaleSpedito += item.quantita
                }
              })
            }
          })
        }
      })
    }
    
    return totaleSpedito
  }

  // Calcola quantità rimanente da spedire per ogni riga
  const getQuantitaRimanente = (rigaId, quantitaTotale) => {
    const giaSpedita = getQuantitaGiaSpedita(rigaId)
    return quantitaTotale - giaSpedita
  }

  // Carica assegnazioni esistenti se presenti (es. tornando indietro)
  useEffect(() => {
    if (data.colli && data.colli.length > 0) {
      const initialAssegnazioni = {}
      data.colli.forEach((collo, colloIdx) => {
        if (collo.righe_ordine && collo.righe_ordine.length > 0) {
          collo.righe_ordine.forEach((assegnazione) => {
            const key = `${assegnazione.riga_ordine_id}_${colloIdx}`
            initialAssegnazioni[key] = assegnazione.quantita
          })
        }
      })
      setAssegnazioni(initialAssegnazioni)
    }
  }, [data.colli])

  const updateAssegnazione = (rigaId, colloIdx, quantita) => {
    const key = `${rigaId}_${colloIdx}`
    const newAssegnazioni = { ...assegnazioni }
    
    if (quantita === '' || quantita === 0) {
      delete newAssegnazioni[key]
    } else {
      newAssegnazioni[key] = parseInt(quantita)
    }
    
    setAssegnazioni(newAssegnazioni)
  }

  const getQuantitaAssegnata = (rigaId) => {
    let totale = 0
    data.colli.forEach((_, colloIdx) => {
      const key = `${rigaId}_${colloIdx}`
      totale += assegnazioni[key] || 0
    })
    return totale
  }

  const validate = () => {
    const newErrors = []

    // 1. Verifica che ogni collo abbia almeno 1 articolo assegnato
    data.colli.forEach((collo, colloIdx) => {
      let totaleProdottiCollo = 0
      
      ordine.righe.forEach((riga) => {
        const key = `${riga.id}_${colloIdx}`
        totaleProdottiCollo += assegnazioni[key] || 0
      })
      
      if (totaleProdottiCollo === 0) {
        newErrors.push(
          `Collo ${colloIdx + 1} (${getDestinatarioNome(collo.destinatario_id) || 'Destinatario'}): deve avere almeno 1 ${soloBoxPersonalizzata ? 'box' : 'articolo'} assegnato`
        )
      }
    })

    // 2. NOTA: Permettiamo spedizioni parziali (non tutte le righe devono essere assegnate)
    // Verifichiamo solo che non si assegni PIÙ della quantità rimanente
    ordine.righe.forEach((riga) => {
      const quantitaAssegnata = getQuantitaAssegnata(riga.id)
      const quantitaRimanente = getQuantitaRimanente(riga.id, riga.quantita)
      
      // Solo errore se si assegna più del rimanente
      if (quantitaAssegnata > quantitaRimanente) {
        newErrors.push(
          `Riga "${riga.descrizione}": assegnate ${quantitaAssegnata} ma rimanenti solo ${quantitaRimanente}`
        )
      }
    })

    setErrors(newErrors)
    return newErrors.length === 0
  }

  const handleNext = () => {
    if (validate()) {
      // Costruisci array righe_ordine per ogni collo
      const colliConRighe = data.colli.map((collo, colloIdx) => {
        const righe_ordine = []
        
        ordine.righe.forEach((riga) => {
          const key = `${riga.id}_${colloIdx}`
          const quantita = assegnazioni[key]
          
          if (quantita && quantita > 0) {
            righe_ordine.push({
              riga_ordine_id: riga.id,
              quantita: quantita,
            })
          }
        })
        
        return {
          ...collo,
          righe_ordine,
        }
      })

      onComplete({ colli: colliConRighe })
    }
  }

  const autoAssegna = () => {
    // Auto-assegna tutte le righe rimanenti al primo collo
    const newAssegnazioni = {}
    ordine.righe.forEach((riga) => {
      const quantitaRimanente = getQuantitaRimanente(riga.id, riga.quantita)
      if (quantitaRimanente > 0) {
        const key = `${riga.id}_0`
        newAssegnazioni[key] = quantitaRimanente
      }
    })
    setAssegnazioni(newAssegnazioni)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Assegna {soloBoxPersonalizzata ? 'Box' : 'Articoli'} ai Colli</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Specifica quante {soloBoxPersonalizzata ? 'box' : 'unità di ogni articolo'} vanno in ciascun collo
              </p>
            </div>
            {data.colli.length === 1 && (
              <Button type="button" onClick={autoAssegna} size="sm" variant="outline">
                Assegna Tutto al Collo 1
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Alert spedizioni esistenti */}
          {ordine.spedizioni && ordine.spedizioni.length > 0 && (
            <Alert>
              <ExclamationTriangleIcon className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium">Spedizioni già create per questo ordine: {ordine.spedizioni.length}</p>
                <p className="text-sm mt-1">
                  Le quantità mostrate sono quelle <strong>rimanenti</strong> da spedire (totale - già spedito).
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* Errori validazione */}
          {errors.length > 0 && (
            <Alert variant="destructive">
              <ExclamationTriangleIcon className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Tabella assegnazioni */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-4 font-medium">{soloBoxPersonalizzata ? 'Box' : 'Articolo'}</th>
                  <th className="text-center p-4 font-medium">Rimanenti</th>
                  {data.colli.map((collo, idx) => (
                    <th key={idx} className="text-center p-4 font-medium align-bottom">
                      <div>Collo {collo.numero_collo}</div>
                      {collo.destinatario_id && (
                        <div
                          className="text-xs font-normal text-muted-foreground mt-1 max-w-[160px] mx-auto truncate"
                          title={getDestinatarioNome(collo.destinatario_id) || ''}
                        >
                          {getDestinatarioNome(collo.destinatario_id) || `ID ${collo.destinatario_id}`}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className="text-center p-4 font-medium">Assegnate</th>
                  <th className="text-center p-4 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody className="readable-rows">
                {ordine.righe.map((riga) => {
                  const quantitaAssegnata = getQuantitaAssegnata(riga.id)
                  const quantitaRimanente = getQuantitaRimanente(riga.id, riga.quantita)
                  const quantitaGiaSpedita = getQuantitaGiaSpedita(riga.id)
                  const isComplete = quantitaAssegnata === quantitaRimanente
                  const isError = quantitaAssegnata > quantitaRimanente

                  return (
                    <tr key={riga.id} className="border-t">
                      <td className="p-4">
                        <div className="font-medium">{riga.descrizione}</div>
                        {riga.variante && (
                          <div className="text-sm text-muted-foreground">
                            {riga.variante}
                          </div>
                        )}
                        {quantitaGiaSpedita > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Già spedite: {quantitaGiaSpedita} / {riga.quantita}
                          </div>
                        )}
                      </td>
                      <td className="text-center p-4">
                        <Badge variant={quantitaRimanente > 0 ? "outline" : "secondary"}>
                          {quantitaRimanente}
                        </Badge>
                      </td>
                      {data.colli.map((collo, colloIdx) => {
                        const key = `${riga.id}_${colloIdx}`
                        return (
                          <td key={colloIdx} className="p-4">
                            <Input
                              type="number"
                              min="0"
                              max={quantitaRimanente}
                              disabled={quantitaRimanente === 0}
                              value={assegnazioni[key] || ''}
                              onChange={(e) =>
                                updateAssegnazione(riga.id, colloIdx, e.target.value)
                              }
                              className="w-20 mx-auto text-center"
                              placeholder="0"
                            />
                          </td>
                        )
                      })}
                      <td className="text-center p-4">
                        <Badge
                          variant={isComplete ? 'default' : isError ? 'destructive' : 'secondary'}
                        >
                          {quantitaAssegnata}
                        </Badge>
                      </td>
                      <td className="text-center p-4">
                        {isComplete ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                        ) : (
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mx-auto" />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Legenda */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-4 w-4 text-green-600" />
              <span>Completamente assegnata</span>
            </div>
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
              <span>Da completare</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Indietro
        </Button>
        <Button type="button" onClick={handleNext}>
          Avanti: Riepilogo
        </Button>
      </div>
    </div>
  )
}
